class OkazoCartToast {
  constructor() {
    this.region = null;
    this.activeToasts = new Set();
    const drawer = document.querySelector("cart-drawer");
    if (drawer) {
      this.drawerObserver = new MutationObserver(() => {
        if (drawer.classList.contains("active")) {
          this.activeToasts.forEach((toast) => this.dismiss(toast));
        }
      });
      this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ["class"] });
    }
  }

  show({ type = "success", title, message, actionLabel, actionUrl, closeLabel }) {
    const toast = document.createElement("div");
    const toastType = type === "error" ? "error" : "success";

    toast.className = `okz-cart-toast okz-cart-toast--${toastType}`;
    toast.setAttribute("role", toastType === "error" ? "alert" : "status");
    toast.setAttribute("aria-live", toastType === "error" ? "assertive" : "polite");
    toast.innerHTML = `
      <span class="okz-cart-toast__mark" aria-hidden="true">
        <span>${toastType === "error" ? "!" : "✓"}</span>
      </span>
      <span class="okz-cart-toast__content">
        <strong>${this.escapeHtml(title || "")}</strong>
        <span>${this.escapeHtml(message || "")}</span>
      </span>
      ${
        actionLabel && actionUrl
          ? `<a class="okz-cart-toast__action" href="${this.escapeAttribute(actionUrl)}">${this.escapeHtml(actionLabel)}</a>`
          : ""
      }
      <button class="okz-cart-toast__close" type="button" aria-label="${this.escapeAttribute(
        closeLabel || "Fermer",
      )}">×</button>
    `;

    const closeButton = toast.querySelector(".okz-cart-toast__close");
    const close = () => this.dismiss(toast);

    closeButton?.addEventListener("click", close);
    toast.querySelector(".okz-cart-toast__action")?.addEventListener("click", (event) => {
      const drawer = document.querySelector("cart-drawer");
      if (!drawer?.open) return;
      event.preventDefault();
      drawer.open(document.querySelector("#cart-icon-bubble"));
    });
    const pause = () => window.clearTimeout(toast.okazoDismissTimeout);
    const resume = () => {
      pause();
      if (!toast.matches(":hover, :focus-within"))
        toast.okazoDismissTimeout = window.setTimeout(close, toastType === "error" ? 5200 : 3800);
    };
    toast.addEventListener("pointerenter", pause);
    toast.addEventListener("focusin", pause);
    toast.addEventListener("pointerleave", resume);
    toast.addEventListener("focusout", () => queueMicrotask(resume));
    this.ensureRegion().append(toast);
    this.activeToasts.add(toast);
    resume();

    if (this.activeToasts.size > 3) {
      const [oldestToast] = this.activeToasts;
      this.dismiss(oldestToast);
    }
  }

  ensureRegion() {
    if (this.region?.isConnected) return this.region;

    this.region = document.querySelector("[data-okz-cart-toast-region]");
    if (!this.region) {
      this.region = document.createElement("div");
      this.region.className = "okz-cart-toast-region";
      this.region.setAttribute("data-okz-cart-toast-region", "");
      this.region.setAttribute("aria-live", "polite");
      this.region.setAttribute("aria-atomic", "false");
      document.body.append(this.region);
    }

    return this.region;
  }

  dismiss(toast) {
    if (!toast || !toast.isConnected) return;

    window.clearTimeout(toast.okazoDismissTimeout);
    toast.classList.add("is-leaving");
    this.activeToasts.delete(toast);
    window.setTimeout(() => toast.remove(), 180);
  }

  escapeHtml(value) {
    const span = document.createElement("span");
    span.textContent = value;
    return span.innerHTML;
  }

  escapeAttribute(value) {
    return this.escapeHtml(value).replace(/"/g, "&quot;");
  }
}

class OkazoAjaxCart {
  constructor() {
    this.toast = new OkazoCartToast();
    this.onSubmit = this.onSubmit.bind(this);
  }

  init() {
    document.addEventListener("submit", this.onSubmit);
  }

  onSubmit(event) {
    const form = event.target?.closest?.("[data-okz-add-to-cart-form]");
    if (!form) return;

    event.preventDefault();
    this.add(form);
  }

  async add(form) {
    const submitButton = form.querySelector("[data-okz-add-to-cart-submit]");
    if (submitButton?.disabled || submitButton?.getAttribute("aria-disabled") === "true") return;

    this.setLoading(submitButton, true);
    form.querySelector("[data-okz-cart-error]")?.remove();

    try {
      const request = this.prepareRequest(form);
      // A stalled request must end in the error state instead of leaving the button spinning.
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 15000);
      const response = await fetch(this.cartAddUrl(form), { ...request.config, signal: controller.signal }).finally(
        () => window.clearTimeout(timeout),
      );
      const payload = await response.json().catch(() => ({}));

      if (!response.ok || payload.status === 422) {
        throw new Error(payload.description || payload.message || form.dataset.okzToastError);
      }

      this.renderCartSections(payload);
      this.setLoading(submitButton, false);
      this.flashSuccess(submitButton, form);
      OkazoCart.publishAdded(
        form,
        payload,
        form.dataset.okzCartSource || "okazo-product-card",
        request.formData.get("id"),
      );

      this.toast.show({
        type: "success",
        title: form.dataset.okzToastTitle,
        message: form.dataset.okzToastMessage,
        actionLabel: form.dataset.okzToastActionLabel,
        actionUrl: window.routes?.cart_url || "/cart",
        closeLabel: form.dataset.okzToastCloseLabel,
      });
    } catch (error) {
      const message = (error?.name !== "AbortError" && error?.message) || form.dataset.okzToastError;
      if (form.closest("dialog[open]")) {
        const status = document.createElement("p");
        status.className = "okz-cart-inline-error";
        status.dataset.okzCartError = "";
        status.setAttribute("role", "alert");
        status.textContent = message;
        form.append(status);
        status.scrollIntoView({ block: "nearest" });
      } else {
        this.toast.show({
          type: "error",
          title: form.dataset.okzToastErrorTitle || form.dataset.okzToastError,
          message,
          closeLabel: form.dataset.okzToastCloseLabel,
        });
      }
    } finally {
      this.setLoading(submitButton, false);
    }
  }

  prepareRequest(form) {
    return OkazoCart.addRequest(form, this.cartSectionsToRender());
  }

  cartAddUrl(form) {
    const root = window.Shopify?.routes?.root;
    if (root) return `${root.replace(/\/?$/, "/")}cart/add.js`;

    const action = form.getAttribute("action");
    if (action) return action;

    return window.routes?.cart_add_url || "/cart/add";
  }

  cartSectionsToRender() {
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer?.getSectionsToRender) return cartDrawer.getSectionsToRender();

    const cartNotification = document.querySelector("cart-notification");
    if (cartNotification?.getSectionsToRender) return cartNotification.getSectionsToRender();

    return [{ id: "cart-icon-bubble" }];
  }

  renderCartSections(payload) {
    const sections = payload?.sections;
    if (!sections) {
      this.refreshCartCount();
      return;
    }

    OkazoCart.renderSections(payload, this.cartSectionsToRender());

    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer) {
      cartDrawer.classList.remove("is-empty");
      if (cartDrawer.querySelector(".cart-drawer__items")) cartDrawer.updateProductRecommendation?.();
    }
  }

  async refreshCartCount() {
    try {
      const root = window.Shopify?.routes?.root || "/";
      const response = await fetch(`${root.replace(/\/?$/, "/")}cart.js`, { headers: { Accept: "application/json" } });
      const cart = await response.json();
      this.updateCartCount(cart.item_count);
    } catch (_error) {
      // The item is already in the cart; keeping the previous bubble is safer than blocking success feedback.
    }
  }

  updateCartCount(count) {
    const bubble = document.querySelector("#cart-icon-bubble .cart-count-bubble");
    if (!bubble || Number.isNaN(Number(count))) return;

    const visibleCount = bubble.querySelector("span[aria-hidden='true']");
    const hiddenCount = bubble.querySelector(".visually-hidden");
    if (visibleCount) visibleCount.textContent = count;
    if (hiddenCount) hiddenCount.textContent = `Panier, ${count} article${Number(count) > 1 ? "s" : ""}`;
    bubble.classList.toggle("visually-hidden", Number(count) >= 100);
  }

  setLoading(button, isLoading) {
    if (!button) return;

    button.disabled = isLoading;
    button.setAttribute("aria-busy", String(isLoading));
    button.classList.toggle("is-loading", isLoading);
  }

  flashSuccess(button, form) {
    if (!button) return;

    window.clearTimeout(button.okazoSuccessTimeout);

    const label = button.querySelector("[data-okz-add-to-cart-label]");
    const baseLabel = button.dataset.okzAddBaseLabel || label?.textContent?.trim() || "";
    const successLabel = button.dataset.okzAddSuccessLabel || form?.dataset.okzAddSuccessLabel || "Ajouté";
    const productTitle = button.dataset.okzProductTitle || form?.dataset.okzProductTitle || "";
    const successAriaLabel = [successLabel, productTitle].filter(Boolean).join(" — ");
    const previousAriaLabel = button.getAttribute("aria-label");

    if (label) label.textContent = successLabel;
    button.classList.add("is-added");
    button.setAttribute("aria-label", successAriaLabel || successLabel);

    button.okazoSuccessTimeout = window.setTimeout(() => {
      if (label && baseLabel) label.textContent = baseLabel;
      button.classList.remove("is-added");
      if (previousAriaLabel) {
        button.setAttribute("aria-label", previousAriaLabel);
      } else {
        button.removeAttribute("aria-label");
      }
    }, 1800);
  }
}

new OkazoAjaxCart().init();

function initOkazoContinueShopping() {
  const storageKey = "okazo-browsing-return";
  const isListing = (url) => /^\/collections\/[^/]+\/?$|^\/search\/?$/.test(url.pathname);

  // Capture the committed URL on departure, including Ajax filters, sorting and pagination.
  window.addEventListener("pagehide", () => {
    if (!document.querySelector("okazo-collection") || !isListing(location)) return;
    try {
      sessionStorage.setItem(storageKey, location.href);
    } catch (_error) {
      // The native catalogue link remains available when session storage is blocked.
    }
  });

  const restoreLink = () => {
    const link = document.querySelector("[data-okz-continue-shopping]");
    if (!link) return;
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (!saved) return;
      const url = new URL(saved);
      if (url.origin === location.origin && isListing(url)) link.href = url.href;
    } catch (_error) {
      // Ignore unavailable storage or an invalid saved destination.
    }
  };
  restoreLink();
  window.addEventListener("pageshow", restoreLink);
}

initOkazoContinueShopping();

class OkazoCartQuantity extends HTMLElement {
  connectedCallback() {
    this.listeners?.abort();
    this.listeners = new AbortController();
    const { signal } = this.listeners;
    this.input = this.querySelector("input");
    this.addEventListener(
      "click",
      (event) => {
        const button = event.target.closest("button");
        if (!button || button.disabled) return;
        event.preventDefault();
        if (button.name === "plus") this.input.stepUp();
        else this.input.stepDown();
        this.input.dispatchEvent(new Event("change", { bubbles: true }));
      },
      { signal },
    );
    this.input.addEventListener(
      "change",
      () => {
        const min = Number(this.input.min) || 1;
        const max = this.input.max ? Number(this.input.max) : Infinity;
        const step = Number(this.input.step) || 1;
        const value = this.input.value === "" ? Number(this.input.defaultValue) : Number(this.input.value);
        const allowedMax = min + Math.floor((max - min) / step) * step;
        this.input.value =
          max < min
            ? this.input.defaultValue
            : Math.min(allowedMax, Math.max(min, min + Math.round((value - min) / step) * step));
        this.sync();
      },
      { signal },
    );
    this.sync();
  }

  disconnectedCallback() {
    this.listeners?.abort();
  }

  sync() {
    this.querySelector('[name="minus"]').disabled =
      this.input.disabled || Number(this.input.value) <= (Number(this.input.min) || 1);
    this.querySelector('[name="plus"]').disabled =
      this.input.disabled ||
      (this.input.max !== "" && Number(this.input.value) + (Number(this.input.step) || 1) > Number(this.input.max));
  }
}

if (!customElements.get("okazo-cart-quantity")) customElements.define("okazo-cart-quantity", OkazoCartQuantity);
